import { AutomationStep } from '@/types/automationSteps.ts';
import { ConditionAction, StepType } from '@/types/automationFields.ts';
import type { RetryAttemptItem } from '@/pages/Run/components/RetryAttemptItems.ts';
import type { RetryAttemptOutcomeTone } from '@/pages/Run/components/retryAttemptPresentation.ts';
import { ChildScopeState, ParentStepRun, StepExecution, StepRun } from '@/types/stepExecutions.ts';
import { ExecutionStatus } from '@/types/statuses.ts';

export type ChildScopeChoice = {
  value: string;
  label: string;
  status?: ExecutionStatus | null;
  childIndex: number;
  attemptNumber?: number;
  parentPath?: string;
  scopeParentStepRunId?: string;
  outcomeLabel?: string;
  outcomeTone?: RetryAttemptOutcomeTone;
  detailText?: string;
  lifecycleText?: string;
  historical?: boolean;
};

export type ChildScopeContext = {
  step: AutomationStep;
  stepRun: StepRun;
  stepExecution: StepExecution;
  parentSteps: ParentStepRun[];
};

export const getChildScopeKey = (context: ChildScopeContext): string =>
  context.stepRun.full_path ||
  [...context.parentSteps.map((parent) => parent.full_path), context.step.step_id].join('/');

// Legacy accordions use `value` for display ordering. The scope stack must instead retain
// a stable API scope identity as live child-scope state changes.
export const getChildScopeChoiceId = (choice: ChildScopeChoice): string =>
  choice.attemptNumber !== undefined
    ? 'attempt:' + choice.childIndex + ':' + choice.attemptNumber
    : 'child:' + choice.childIndex;

export const getNearestChildScopeChoiceId = (
  items: ChildScopeChoice[],
  previousChoiceId: string | null,
): string | null => {
  if (items.length === 0) return null;
  if (!previousChoiceId) return getChildScopeChoiceId(items[0]);
  const previousParts = previousChoiceId.split(':');
  const isAttempt = previousParts[0] === 'attempt';
  const previousIndex = Number(previousParts.at(-1));
  const comparableItems = items.filter((item) =>
    isAttempt ? item.attemptNumber !== undefined : item.attemptNumber === undefined,
  );
  if (!Number.isFinite(previousIndex) || comparableItems.length === 0) {
    return getChildScopeChoiceId(items[0]);
  }
  const nearestItem = comparableItems.reduce((nearest, item) => {
    const nearestIndex = isAttempt ? nearest.attemptNumber! : nearest.childIndex;
    const itemIndex = isAttempt ? item.attemptNumber! : item.childIndex;
    return Math.abs(itemIndex - previousIndex) < Math.abs(nearestIndex - previousIndex)
      ? item
      : nearest;
  });
  return getChildScopeChoiceId(nearestItem);
};

export const getChildScopeParentSteps = (context: ChildScopeContext): ParentStepRun[] => [
  ...context.parentSteps,
  { ...context.stepRun, step_execution: context.stepExecution },
];

export const buildChildScopeItems = (
  iterations: ChildScopeState[],
  parentSteps: ParentStepRun[],
  stepsById: Record<string, AutomationStep>,
): ChildScopeChoice[] => {
  const parentStepRun = parentSteps.at(-1);
  const step = parentStepRun ? stepsById[parentStepRun.step_id] : null;
  let displayIndex = 0;

  return iterations.flatMap((iteration, zeroIndex) => {
    const childIndex = zeroIndex + 1;
    let iterationValue = iteration.key;
    if (parentStepRun?.step_type === StepType.LocalSubflowCall) {
      iterationValue = 'Module execution';
    }
    if (parentStepRun?.step_type === StepType.FailureHandler) {
      if (iteration.key === 'protected') iterationValue = 'Original steps';
      if (iteration.key === 'handler') iterationValue = 'Response steps';
    }
    if (step?.step_type === StepType.Conditional) {
      const condition = step.conditions.find(
        (candidate) => candidate.condition_id === iteration.key,
      );
      if (condition) {
        if (condition.action !== ConditionAction.ChildSteps) return [];
        const conditionIndex = step.conditions.findIndex(
          (candidate) => candidate.condition_id === iteration.key,
        );
        iterationValue = 'Condition ' + (conditionIndex + 1) + ': ' + condition.name;
      }
    }
    displayIndex += 1;
    return [
      {
        value: displayIndex.toString(),
        label: iterationValue ? displayIndex + ' - ' + iterationValue : displayIndex.toString(),
        status: iteration.status,
        childIndex,
      },
    ];
  });
};

export const getChildScopeHeading = (stepType: StepType, stepName: string): string => {
  if (stepType === StepType.Retry) return `Attempts for “${stepName}”`;
  if (stepType === StepType.Conditional) return `Branches from “${stepName}”`;
  if (stepType === StepType.LocalSubflowCall) return `Steps in “${stepName}”`;
  return `Children of “${stepName}”`;
};

export const getChildScopeActionLabel = (stepType: StepType): string => {
  if (stepType === StepType.Retry) return 'View attempts';
  if (stepType === StepType.FailureHandler) return 'View child results';
  if (stepType === StepType.Conditional) return 'View branches';
  if (stepType === StepType.LocalSubflowCall) return 'View module steps';
  return 'View child runs';
};

export const asChildScopeChoices = (items: RetryAttemptItem[]): ChildScopeChoice[] => items;
